import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, forwardRef } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'ui-checkbox',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiCheckboxComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <input
      type="checkbox"
      [id]="id"
      [attr.name]="name"
      [checked]="modelChecked"
      (change)="onChangeEvent($event)"
      (blur)="onBlurEvent()"
      [disabled]="disabled || disabledByForm"
      [required]="required"
      [class]="checkboxClass"
      [attr.aria-label]="ariaLabel"
    />
  `,
})
export class UiCheckboxComponent implements ControlValueAccessor {
  @Input() id?: string;
  @Input() name?: string;
  @Input() checked = false;
  @Input() disabled = false;
  @Input() required = false;
  @Input() ariaLabel?: string;
  @Input() checkboxClass = 'w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500';
  @Input() formControl?: FormControl;
  @Input() ngModel?: any;

  @Output() ngModelChange = new EventEmitter<any>();
  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() change = new EventEmitter<Event>();

  modelChecked = false;
  disabledByForm = false;
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  writeValue(value: any): void {
    this.modelChecked = !!value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm = isDisabled;
  }

  onChangeEvent(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.modelChecked = checked;
    this.ngModelChange.emit(checked);
    this.onChangeFn(checked);
    this.checkedChange.emit(checked);
    this.change.emit(event);
  }

  onBlurEvent(): void {
    this.onTouchedFn();
  }

  onChange(event: Event): void {
    this.change.emit(event);
    this.checkedChange.emit((event.target as HTMLInputElement).checked);
  }
}
